import { useState, useEffect } from 'react';
import { financeiroService } from '../../services';
import { formatCurrency, formatDateTime } from '../../utils/format';
import './Financeiro.css';

function hojeISO() {
  return new Date().toISOString().slice(0, 10);
}

export default function Financeiro() {
  const [mes, setMes] = useState(new Date().toISOString().slice(0, 7));
  const [resumo, setResumo] = useState(null);
  const [dre, setDre] = useState(null);
  const [tab, setTab] = useState('resumo');
  const [loading, setLoading] = useState(true);

  // Caixas do dia
  const [dataCaixas, setDataCaixas] = useState(hojeISO());
  const [caixasDia, setCaixasDia] = useState(null);
  const [loadingCaixas, setLoadingCaixas] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      financeiroService.resumo(mes),
      financeiroService.dre(mes),
    ]).then(([r, d]) => {
      setResumo(r.data);
      setDre(d.data);
    }).catch(() => {})
    .finally(() => setLoading(false));
  }, [mes]);

  useEffect(() => {
    if (tab !== 'caixas') return;
    setLoadingCaixas(true);
    financeiroService.caixasDia(dataCaixas)
      .then(res => setCaixasDia(res.data))
      .catch(() => setCaixasDia(null))
      .finally(() => setLoadingCaixas(false));
  }, [tab, dataCaixas]);

  const labelStatusCaixa = (s) => s === 'aberto' ? 'Aberto' : 'Fechado';

  if (loading) return <div className="loading"><div className="spinner"></div>Carregando...</div>;

  return (
    <div className="financeiro-page">
      <div className="page-header">
        <h1>💵 Financeiro</h1>
        {tab === 'caixas' ? (
          <input type="date" value={dataCaixas} onChange={e => setDataCaixas(e.target.value)} />
        ) : (
          <input type="month" value={mes} onChange={e => setMes(e.target.value)} />
        )}
      </div>

      <div className="fin-tabs">
        <button className={tab === 'resumo' ? 'active' : ''} onClick={() => setTab('resumo')}>Resumo</button>
        <button className={tab === 'dre' ? 'active' : ''} onClick={() => setTab('dre')}>DRE</button>
        <button className={tab === 'caixas' ? 'active' : ''} onClick={() => setTab('caixas')}>Caixas do dia</button>
      </div>

      {tab === 'resumo' && resumo && (
        <div className="fin-resumo-grid">
          <div className="card fin-card fin-receitas">
            <small>Receitas (líquidas)</small>
            <strong>{formatCurrency(resumo.receitas)}</strong>
          </div>
          <div className="card fin-card fin-despesas">
            <small>Despesas</small>
            <strong>{formatCurrency(resumo.despesas)}</strong>
          </div>
          <div className="card fin-card fin-taxas">
            <small>Taxas de cartão (total)</small>
            <strong style={{ color: 'var(--danger)' }}>{formatCurrency(resumo.taxas_cartao || 0)}</strong>
          </div>
          <div className="card fin-card fin-lucro">
            <small>Lucro Líquido</small>
            <strong style={{color: resumo.lucro_liquido >= 0 ? 'var(--success)' : 'var(--danger)'}}>{formatCurrency(resumo.lucro_liquido)}</strong>
          </div>
          <div className="card fin-card">
            <small>Margem Líquida</small>
            <strong>{resumo.margem_liquida}%</strong>
          </div>
          <div className="card fin-card">
            <small>Vendas</small>
            <strong>{resumo.qtd_vendas}</strong>
          </div>
          <div className="card fin-card">
            <small>Ticket Médio</small>
            <strong>{formatCurrency(resumo.ticket_medio)}</strong>
          </div>
        </div>
      )}

      {tab === 'dre' && dre && (
        <div className="card dre-table">
          <h3>Demonstrativo de Resultados - {mes}</h3>
          <table>
            <tbody>
              <tr><td>Receita Bruta</td><td className="right">{formatCurrency(dre.receita_bruta)}</td></tr>
              {dre.taxas_cartao > 0.005 && (
                <tr className="sub"><td>(-) Taxas de cartão</td><td className="right danger">{formatCurrency(dre.taxas_cartao)}</td></tr>
              )}
              <tr className="sub"><td>(-) Impostos (~6%)</td><td className="right danger">{formatCurrency(dre.impostos)}</td></tr>
              <tr className="bold"><td>Receita Líquida</td><td className="right">{formatCurrency(dre.receita_liquida)}</td></tr>
              <tr className="sub"><td>(-) CMV</td><td className="right danger">{formatCurrency(dre.cmv)}</td></tr>
              <tr className="bold"><td>Lucro Bruto</td><td className="right">{formatCurrency(dre.lucro_bruto)}</td></tr>
              <tr className="sub"><td>(-) Despesas Operacionais</td><td className="right danger">{formatCurrency(dre.despesas_operacionais)}</td></tr>
              <tr className="bold total"><td>Lucro Líquido</td><td className="right" style={{color: dre.lucro_liquido >= 0 ? 'var(--success)' : 'var(--danger)'}}>{formatCurrency(dre.lucro_liquido)}</td></tr>
              <tr><td>Margem</td><td className="right">{dre.margem}%</td></tr>
            </tbody>
          </table>
        </div>
      )}

      {tab === 'caixas' && (
        <div>
          {loadingCaixas ? (
            <div className="loading"><div className="spinner"></div>Carregando...</div>
          ) : !caixasDia || caixasDia.caixas.length === 0 ? (
            <div className="card" style={{ textAlign: 'center', padding: 40, color: '#aaa' }}>
              Nenhum caixa aberto nesta data.
            </div>
          ) : (
            <>
              <div className="fin-resumo-grid" style={{ marginBottom: 16 }}>
                <div className="card fin-card fin-receitas">
                  <small>Total de vendas (todos os caixas)</small>
                  <strong>{formatCurrency(caixasDia.total_geral)}</strong>
                </div>
                {caixasDia.total_geral - (caixasDia.total_geral_liquido ?? caixasDia.total_geral) > 0.005 && (
                  <div className="card fin-card">
                    <small>Total líquido (em conta)</small>
                    <strong>{formatCurrency(caixasDia.total_geral_liquido)}</strong>
                  </div>
                )}
                <div className="card fin-card">
                  <small>Caixas no dia</small>
                  <strong>{caixasDia.qtd_caixas}</strong>
                </div>
              </div>

              <div className="card">
                <div className="table-container">
                  <table>
                    <thead>
                      <tr>
                        <th>Caixa</th>
                        <th>Status</th>
                        <th>Abertura</th>
                        <th>Fechamento</th>
                        <th className="right">Saldo inicial</th>
                        <th className="right">Total vendas</th>
                        <th className="right">Líquido</th>
                        <th className="right">Contado / Diferença</th>
                      </tr>
                    </thead>
                    <tbody>
                      {caixasDia.caixas.map(c => (
                        <tr key={c.id}>
                          <td>#{c.id}<br /><small style={{ color: '#888' }}>{c.usuario_abertura}</small></td>
                          <td>
                            <span className={`badge ${c.status === 'aberto' ? 'badge-warning' : 'badge-success'}`}>
                              {labelStatusCaixa(c.status)}
                            </span>
                          </td>
                          <td>{formatDateTime(c.aberto_em)}</td>
                          <td>{c.fechado_em ? formatDateTime(c.fechado_em) : '—'}</td>
                          <td className="right">{formatCurrency(c.saldo_inicial)}</td>
                          <td className="right" style={{ fontWeight: 700, color: 'var(--primary, #D4380D)' }}>
                            {formatCurrency(c.total_vendas)}
                          </td>
                          <td className="right" style={{ color: 'var(--success)' }}>
                            {formatCurrency(c.total_liquido ?? c.total_vendas)}
                          </td>
                          <td className="right">
                            {c.status === 'fechado' ? (
                              <>
                                {formatCurrency(c.valor_contado)}
                                <br />
                                <small style={{ color: Math.abs(parseFloat(c.diferenca || 0)) < 0.005 ? 'var(--success)' : 'var(--danger)' }}>
                                  {parseFloat(c.diferenca || 0) > 0 ? '+' : ''}{formatCurrency(c.diferenca)}
                                </small>
                              </>
                            ) : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bold">
                        <td colSpan={5}>Total geral</td>
                        <td className="right" style={{ fontWeight: 700 }}>{formatCurrency(caixasDia.total_geral)}</td>
                        <td className="right" style={{ fontWeight: 700, color: 'var(--success)' }}>{formatCurrency(caixasDia.total_geral_liquido ?? caixasDia.total_geral)}</td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
